import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, withStyles, emphasize } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { lighten } from '@material-ui/core/styles/colorManipulator';

import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import LinearProgress from '@material-ui/core/LinearProgress';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import MenuList from '@material-ui/core/MenuList';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Chip from '@material-ui/core/Chip';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import EditIcon from '@material-ui/icons/Edit'
import WallpaperIcon from '@material-ui/icons/Wallpaper';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import UploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import InputIcon from '@material-ui/icons/Input';
import StorageIcon from '@material-ui/icons/Storage';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import FolderIcon from '@material-ui/icons/Folder';
import PublicIcon from '@material-ui/icons/Public';

import EditFileDialog from 'app/components/drive/EditFileDialog';
import EditFolderDialog from 'app/components/drive/EditFolderDialog';
import MoveItemDialog from 'app/components/drive/MoveItemDialog';
import RedButton from 'app/components/buttons/RedButton';
import DriveTree from 'app/components/drive/DriveTree';

import FileTypes from 'app/utils/FileTypes';

import * as Icons from '@material-ui/icons'

import { containerFull, containerFlexFixed, containerFlexGrow } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const StyledBreadcrumb = withStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.grey[100],
        height: theme.spacing(3),
        color: theme.palette.grey[800],
        fontWeight: theme.typography.fontWeightRegular,
        padding: theme.spacing(2),
        '&:hover, &:focus': {
            backgroundColor: theme.palette.grey[300],
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(theme.palette.grey[300], 0.12),
        },
    },
  }))(Chip);

const useTableRowStyles = makeStyles(theme => ({
    tableCell: {
        whiteSpace: 'nowrap',
        userSelect: 'none',
        cursor: 'default'
    },
    rowIcon: {
        marginRight: theme.spacing(3),
        verticalAlign: 'middle',
        display: 'inline-block',
        lineHeight: 'normal',
        color: theme.palette.text.secondary
    },
    rowName: {
        verticalAlign: 'middle',
        display: 'inline-block'
    },
    selected: {
        backgroundColor: lighten(theme.palette.primary.main, 0.85) + ' !important'
    },
    imagePreview: {
        width: 'auto',
        height: 'auto',
        maxWidth: 100,
        maxHeight: 60,
        display: 'inlineBlock',
        verticalAlign: 'middle',
        marginRight: theme.spacing(3)
    }
}));

function AdvancedTableRow(props) {

    const classes = useTableRowStyles();

    const { item, selected, onClick, onDoubleClick } = props;

    const Icon = useMemo(() => {
        if ('extension' in item) {
            const FileType = FileTypes.find(obj => obj.ext === item.extension);
            return Icons[FileType.icon ?? 'Description'];
        } else return Icons['Folder'];

    }, [item]);

    const isImage = useMemo(() => {
        if ('extension' in item) {
            const FileType = FileTypes.find(obj => obj.ext == item.extension);
            if (FileType.icon === 'Image') return true;
        }
        return false;
    }, [item])

    return (
        <TableRow
            hover
            selected={selected}
            onClick={onClick}
            onContextMenu={onClick}
            classes={{ selected: classes.selected}}
            onDoubleClick={onDoubleClick}
            >
            <TableCell scope="row" className={classes.tableCell}>
                {isImage ? (
                    <img src={item.full_url} className={classes.imagePreview}/>
                ) : (
                    <span className={classes.rowIcon}><Icon /></span>
                )}
                <Typography variant="subtitle2" className={classes.rowName} color="textSecondary">
                    {item.name}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography variant="subtitle2" color="textSecondary">
                    {item.access}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography variant="subtitle2" color="textSecondary">
                    {DateTime.fromISO(item.updated_at).toLocaleString(DateTime.DATE_MED)}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography variant="subtitle2" color="textSecondary">
                    {item.downloads ?? '-'}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography variant="subtitle2" color="textSecondary">
                    {item.size ? item.size_formatted : '-'}
                </Typography>
            </TableCell>
        </TableRow>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFull(theme),
    },
    flex: {
        display: 'flex'
    },
    nav: {
        ...containerFlexFixed(theme),
        flexBasis: 300,
        paddingRight: theme.spacing(3),
    },
    navItem: {
        padding: theme.spacing(3)
    },
    buttonGroup: {
        borderRadius: 40,
    },
    uploadButton: {
        borderTopRightRadius: 40,
        borderTopLeftRadius: 40
    },
    newButton: {
        borderBottomRightRadius: 40,
        borderBottomLeftRadius: 40
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        overflowX: 'scroll',
        paddingBottom: 10,

        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        }
    },
    emptyBorderPrimary: {
        border: '2px solid',
        borderColor: lighten(theme.palette.secondary.main, 0.62)
    },
    toolbar: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        borderBottom: '2px solid',
        borderBottomColor: theme.palette.divider
    },
    tableWrapper: {

    },
    tableCellHead: {
        whiteSpace: "nowrap",
        paddingRight: '32px'
    },
    tableCellHeadEdit: {
        whiteSpace: "nowrap",
        paddingRight: '10px'
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    popper: {
        zIndex: theme.zIndex.modal
    },
    contextMenuPaper: {
        width: '320px',
        maxWidth: '100%',
        maxHeight: '480px',
        overflowY: 'scroll'
    },
    link: {
        cursor: 'pointer'
    }
}));

function descendingComparator(a, b, orderBy) {
    if(! b[orderBy] && ! a[orderBy]) return 0;

    if(b[orderBy] && ! a[orderBy]) return -1;

    if(! b[orderBy] && a[orderBy]) return 1;

    if (b[orderBy] < a[orderBy]) return -1;

    if (b[orderBy] > a[orderBy]) return 1;

    return 0;
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

export default function Drive(props) {

    const classes = useStyles();
    const api = useApi();
    const { app, hydrateApp } = useApp();

    const [ items, setItems ] = useState([]);
    const [ folders, setFolders ] = useState([]);
    const [ folderId, setFolderId ] = useState(0);
    const [ selectedRowKey, setSelectedRowKey ] = useState(null);

    const [ editingFolder, setEditingFolder ] = useState(null);
    const [ editingFile, setEditingFile ] = useState(null);
    const [ deletingFileId, setDeletingFileId ] = useState(0);

    const [ editFileDialogOpen, setEditFileDialogOpen ] = useState(false);
    const [ editFolderDialogOpen, setEditFolderDialogOpen ] = useState(false);
    const [ deleteFileDialogOpen, setDeleteFileDialogOpen ] = useState(false);
    const [ moveItemDialogOpen, setMoveItemDialogOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);
    const [ foldersLoading, setFoldersLoading ] = useState(false);

    const [ orderBy, setOrderBy ] = useState(null);
    const [ order, setOrder ] = useState('asc');

    const [ contextMenuOpen, setContextMenuOpen ] = useState(false);
    const [ stablePosition, setStablePosition ]  = useState({ x: undefined, y: undefined });

    const getItems = () => {
        setLoading(true);
        setSelectedRowKey(null);

        api.get(`/admin/drive/${folderId}`)
            .then(res => setItems(res.items))
            .finally(() => setLoading(false));
    }

    const getFolders = () => {
        setFoldersLoading(true);

        api.get(`/admin/folder`)
            .then(res => setFolders(res.folders))
            .finally(() => setFoldersLoading(false));
    }

    useEffect(() => getItems(), [folderId]);

    useEffect(() => getFolders(), []);

    // Select Row Handler

    const handleRowClick = (e, key) => {
        if (e.type === 'contextmenu') {
            e.preventDefault();
            if (contextMenuOpen) {
                handleContextMenuClose();
            } else {
                setStablePosition({ x: e.pageX, y: e.pageY });
                setContextMenuOpen(true);
            }
        }

        setSelectedRowKey(key);
    }

    const handleRowDoubleClick = key => {
        if ('downloads' in items[key]) handleDownloadFile(items[key].id);
        else handleFolder(items[key].id);
    }

    const handleDownloadFile = id => {
        setLoading(true)
        api.get(`/admin/file/${id}/download`).finally(() => setLoading(false));
    }

    const handleFolder = id => {
        setFolderId(id);
    }

    // Editing File Dialog Functions

    const openEditFileDialog = () => setEditFileDialogOpen(true);

    const closeEditFileDialog = () => setEditFileDialogOpen(false);

    const closeEditFileDialogAndRefresh = () => {
        closeEditFileDialog();
        getItems();
    }

    // Editing Folder Dialog Functions

    const openEditFolderDialog = () => setEditFolderDialogOpen(true);

    const closeEditFolderDialog = () => setEditFolderDialogOpen(false);

    const closeEditFolderDialogAndRefresh = () => {
        closeEditFolderDialog();
        setEditingFolder(null);
        getItems();
        getFolders();
    }

    // Delete Dialog Functions

    const openDeleteFileDialog = id => {
        setDeleteFileDialogOpen(true);
        setDeletingFileId(id);
    }

    const closeDeleteFileDialog = () => {
        setDeleteFileDialogOpen(false);
        setDeletingFileId(0);
    }

    const closeDeleteFileDialogAndRefresh = () => {
        closeDeleteFileDialog();
        getItems();
    }

    const deleteFile = () => {

        setLoading(true);

        api.delete('/admin/file/' + deletingFileId)
            .then(() => closeDeleteFileDialogAndRefresh())
            .finally(() => setLoading(false));
    }

    const deleteFolder = id => {

        setLoading(true);
        setSelectedRowKey(null);

        api.delete('/admin/folder/' + id)
            .then(() => {
                getItems();
                getFolders();
            }).finally(() => setLoading(false));
    }

    // Move Item Dialog

    const closeMoveItemDialog = () => {
        setMoveItemDialogOpen(false);
    }

    const closeMoveItemDialogAndRefresh = () => {
        closeMoveItemDialog();
        getItems();
    }

    // Sorting and ordering

    const handleRequestSort = property => {
        const isAsc = orderBy === property && order === 'asc';

        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Context Menu Functions

    const handleContextMenuClose = () => {
        setContextMenuOpen(false);
    }

    const handleContextEdit = () => {
        const item = items[selectedRowKey];

        if ('downloads' in item) {
            setEditingFile(item);
            setEditFileDialogOpen(true);
        } else {
            setEditingFolder(item);
            setEditFolderDialogOpen(true);
        }

        handleContextMenuClose();
    }

    const handleContextDownload = () => {
        handleDownloadFile(items[selectedRowKey].id);
        handleContextMenuClose();
    }

    const handleContextMove = () => {
        setMoveItemDialogOpen(true);
        handleContextMenuClose();
    }

    const handleContextSetLogo = () => {
        setLoading(true);
        handleContextMenuClose();

        api.post('/admin/club/branding/logo', {logo_id: items[selectedRowKey].id})
            .then(hydrateApp(true))
            .finally(setLoading(false));
    }

    const toggleVisibility = () => {
        setLoading(true);
        handleContextMenuClose();

        api.get(`/admin/file/${items[selectedRowKey].id}/visibility`).finally(setLoading(false));
    }

    const handleContextDelete = () => {
        const item = items[selectedRowKey];

        handleContextMenuClose();

        if ('downloads' in item) {
            openDeleteFileDialog(item.id)
        } else {
            deleteFolder(item.id)
        }
    }

    const isImage = item => {
        if ('extension' in item) {
            const fileType = FileTypes.find(obj => obj.ext === item.extension);
            return (fileType.icon === 'Image');
        } else return false;
    }

    const flatFolders = useMemo(() => {

        const flat = [];

        const addItemsRecursively = items => {
            items.forEach(item => {
                flat.push(item);

                if (item.folders_recursive && item.folders_recursive.length) {
                    addItemsRecursively(item.folders_recursive);
                }
            });
        }

        if (folders.length > 0) {
            addItemsRecursively(folders);
        }

        return flat;

    }, [folders]);

    const breadCrumbs = useMemo(() => {
        const crumbs = [];

        const addFolderRecursively = (f, init) => {
            crumbs.push({
                label: f.name,
                icon: init ? <FolderOpenIcon size="small"/> : <FolderIcon size="small"/>,
                onclick: init ? null : () => handleFolder(f.id)
            });

            if (f.folder_id > 0) {
                addFolderRecursively(flatFolders.find(ff => ff.id === f.folder_id), false)
            }
        }

        if (flatFolders.length > 0 && folderId > 0) {
            addFolderRecursively(flatFolders.find(f => f.id === folderId), true);
        }

        crumbs.push({
            label: app.club.name,
            icon: <StorageIcon size="small"/>,
            onclick: (folderId === 0) ? null : () => handleFolder(0)
        });

        return crumbs.reverse()

    }, [flatFolders, folderId]);

    const currentGroupId = useMemo(() => {

        if (flatFolders.length > 0 && folderId > 0) {
            return flatFolders.find(f => f.id === folderId).group_id;
        }

        return 0;
    }, [flatFolders, folderId]);

    const ContextMenu = (
        <Popper
            anchorEl={{
                clientHeight: 0,
                clientWidth: 0,
                getBoundingClientRect: () => ({
                    top: stablePosition.y,
                    left: stablePosition.x,
                    right: stablePosition.x,
                    bottom: stablePosition.y,
                    width: 0,
                    height: 0,
                })
            }}
            open={contextMenuOpen}
            onClose={handleContextMenuClose}
            role={undefined}
            transition
            className={classes.popper}
            placement='bottom-start'
            >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'right top'}}>
                    <Paper className={classes.contextMenuPaper}>
                        {(items.length > 0 && selectedRowKey !== null) &&
                            <ClickAwayListener onClickAway={handleContextMenuClose}>
                                <MenuList id="context-menu">
                                    <MenuItem button onClick={handleContextEdit}>
                                        <ListItemIcon>
                                            <EditIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Edit Details" />
                                    </MenuItem>
                                    {('downloads' in items[selectedRowKey]) &&
                                        <MenuItem button onClick={handleContextDownload}>
                                            <ListItemIcon>
                                                <DownloadIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Download File" />
                                        </MenuItem>
                                    }
                                    {isImage(items[selectedRowKey]) &&
                                        <MenuItem button onClick={handleContextSetLogo}>
                                            <ListItemIcon>
                                                <WallpaperIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Set as Logo" />
                                        </MenuItem>
                                    }
                                    <MenuItem button onClick={handleContextMove}>
                                        <ListItemIcon>
                                            <InputIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Move To" />
                                    </MenuItem>
                                    <MenuItem button onClick={toggleVisibility}>
                                        <ListItemIcon>
                                            <PublicIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Toggle Visibility" />
                                    </MenuItem>
                                    <MenuItem button onClick={handleContextDelete}>
                                        <ListItemIcon>
                                            <DeleteIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Delete" />
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        }
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    return (
        <div className={classes.container}>
            { loading ? (
                <LinearProgress color="secondary"/>
            ) : (
                <div className={classes.emptyBorderPrimary}/>
            )}
            <div className={classes.flex}>
                <div className={classes.nav}>
                    <div className={classes.navItem}>
                        <ButtonGroup
                            orientation="vertical"
                            color="primary"
                            aria-label="Create a new folder or upload a new file"
                            variant="contained"
                            fullWidth
                            size="large"
                            className={classes.buttonGroup}
                            >
                            <Button
                                aria-label="Upload a File"
                                onClick={() => openEditFileDialog(null)}
                                startIcon={<UploadIcon />}
                                className={classes.uploadButton}
                                >
                                Upload File
                            </Button>
                            <Button
                                aria-label="Create a Folder"
                                startIcon={<CreateNewFolderIcon />}
                                onClick={() => openEditFolderDialog(null)}
                                className={classes.newButton}
                                >
                                New Folder
                            </Button>
                        </ButtonGroup>
                    </div>
                    <DriveTree
                        folders={folders}
                        selected={folderId}
                        onSelect={handleFolder}
                    />
                </div>
                <div className={classes.content}>
                    <div className={classes.toolbar}>
                        <Breadcrumbs aria-label="breadcrumb">
                            {breadCrumbs.map((crumb, i) => (
                                <StyledBreadcrumb
                                    key={i}
                                    component="button"
                                    label={crumb.label}
                                    icon={crumb.icon}
                                    onClick={crumb.onclick}
                                />
                            ))}
                        </Breadcrumbs>
                    </div>
                    <div className={classes.tableWrapper}>
                        {items.length ? (
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sortDirection={orderBy === 'name' ? order : false} className={classes.tableCellHead}>
                                            <TableSortLabel
                                                active={orderBy === 'name'}
                                                direction={orderBy === 'name' ? order : 'asc'}
                                                onClick={() => handleRequestSort('name')}
                                                >
                                                <Typography variant='subtitle2'>Name</Typography>
                                                {orderBy === 'name' ? (
                                                    <span className={classes.visuallyHidden}>
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={orderBy === 'access' ? order : false} className={classes.tableCellHead}>
                                            <TableSortLabel
                                                active={orderBy === 'access'}
                                                direction={orderBy === 'access' ? order : 'asc'}
                                                onClick={() => handleRequestSort('access')}
                                                >
                                                <Typography variant='subtitle2'>Access</Typography>
                                                {orderBy === 'access' ? (
                                                    <span className={classes.visuallyHidden}>
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={orderBy === 'updated_at' ? order : false} className={classes.tableCellHead}>
                                            <TableSortLabel
                                                active={orderBy === 'updated_at'}
                                                direction={orderBy === 'updated_at' ? order : 'asc'}
                                                onClick={() => handleRequestSort('updated_at')}
                                                >
                                                <Typography variant='subtitle2'>Last Updated</Typography>
                                                {orderBy === 'updated_at' ? (
                                                    <span className={classes.visuallyHidden}>
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={orderBy === 'downloads' ? order : false} className={classes.tableCellHead}>
                                            <TableSortLabel
                                                active={orderBy === 'downloads'}
                                                direction={orderBy === 'downloads' ? order : 'asc'}
                                                onClick={() => handleRequestSort('downloads')}
                                                >
                                                <Typography variant='subtitle2'># of Downloads</Typography>
                                                {orderBy === 'downloads' ? (
                                                    <span className={classes.visuallyHidden}>
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={orderBy === 'size' ? order : false} className={classes.tableCellHead}>
                                            <TableSortLabel
                                                active={orderBy === 'size'}
                                                direction={orderBy === 'size' ? order : 'asc'}
                                                onClick={() => handleRequestSort('size')}
                                                >
                                                <Typography variant='subtitle2'>File Size</Typography>
                                                {orderBy === 'size' ? (
                                                    <span className={classes.visuallyHidden}>
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stableSort(items, getComparator(order, orderBy)).map((item, key) => (
                                        <AdvancedTableRow
                                            onClick={(e) => handleRowClick(e, key)}
                                            onDoubleClick={() => handleRowDoubleClick(key)}
                                            item={item}
                                            key={key}
                                            selected={selectedRowKey === key}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                This folder is empty. To upload your first file, <Link className={classes.link} color="primary" onClick={() => openEditFileDialog(null)}>click here</Link>.
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
            {ContextMenu}
            <EditFileDialog
                open={editFileDialogOpen}
                onSave={closeEditFileDialogAndRefresh}
                onClose={closeEditFileDialog}
                initFolderId={folderId}
                initGroupId={currentGroupId}
                initFile={editingFile}
            />
            <EditFolderDialog
                open={editFolderDialogOpen}
                onSave={closeEditFolderDialogAndRefresh}
                onClose={closeEditFolderDialog}
                folderId={folderId}
                initFolder={editingFolder}
                initGroupId={currentGroupId}
            />
            <MoveItemDialog
                open={moveItemDialogOpen}
                onMove={closeMoveItemDialogAndRefresh}
                onClose={closeMoveItemDialog}
                item={items[selectedRowKey]}
                folders={flatFolders}
                initFolderId={folderId}
            />
            <Dialog
                open={deleteFileDialogOpen}
                onClose={closeDeleteFileDialog}
                aria-labelledby="delete-file-title"
                aria-describedby="delete-file-description"
                fullWidth
                maxWidth="sm"
                >
                <DialogTitle id="delete-file-title">Delete File</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-file-description">
                        Are you sure you want to delete this file?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteFileDialog} color="primary" autoFocus>
                        Cancel
                    </Button>
                    <RedButton onClick={deleteFile} variant="contained">
                        Delete
                    </RedButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}
